import {
  getList,
  getBoolean,
  getOptionalBoolean,
} from "@surya-digital/leo-ts-types";
import { OpenEndedFieldV1, OpenEndedFieldV1DTO } from "./openEndedFieldV1";

export interface OpenEndedQuestionV1DTO {
  options: OpenEndedFieldV1DTO[];
  isChoiceResetAllowed?: boolean | null;
  isMultiLineTextEntryAllowed?: boolean | null;
  isUnanswered?: boolean | null;
}

export class OpenEndedQuestionV1 {
  readonly isOpenEndedQuestionV1OpenEndedQuestionV1: boolean = true;

  readonly options: OpenEndedFieldV1[];
  readonly isChoiceResetAllowed: boolean | null;
  readonly isMultiLineTextEntryAllowed: boolean | null;
  readonly isUnanswered: boolean | null;

  static readonly constraints = {
    "options": {
      "minSize": 1,
      "maxSize": 16,
    },
  }

  constructor(
  options: OpenEndedFieldV1[], 
  isChoiceResetAllowed: boolean | null = null, 
  isMultiLineTextEntryAllowed: boolean | null = null, 
  isUnanswered: boolean | null = null
  ) {
    OpenEndedQuestionV1.validateOptions(options);
    this.options = options;
    this.isChoiceResetAllowed = isChoiceResetAllowed;
    this.isMultiLineTextEntryAllowed = isMultiLineTextEntryAllowed;
    this.isUnanswered = isUnanswered;
  }

  static fromDTO(dto: OpenEndedQuestionV1DTO): OpenEndedQuestionV1 {
    const options = getList<OpenEndedFieldV1DTO, OpenEndedFieldV1>(
      dto,
      "options",
      OpenEndedFieldV1.fromDTO,
      InvalidOpenEndedQuestionV1OptionsError
    );
    const isChoiceResetAllowed = getOptionalBoolean(dto, "isChoiceResetAllowed", InvalidOpenEndedQuestionV1IsChoiceResetAllowedError);
    const isMultiLineTextEntryAllowed = getOptionalBoolean(dto, "isMultiLineTextEntryAllowed", InvalidOpenEndedQuestionV1IsMultiLineTextEntryAllowedError);
    const isUnanswered = getOptionalBoolean(dto, "isUnanswered", InvalidOpenEndedQuestionV1IsUnansweredError);
    return new OpenEndedQuestionV1(
      options,
      isChoiceResetAllowed,
      isMultiLineTextEntryAllowed,
      isUnanswered
    );
  }

  toDTO(): OpenEndedQuestionV1DTO {
    const dto: OpenEndedQuestionV1DTO = {
        options: this.options.map((e: OpenEndedFieldV1) => { return e.toDTO() }),
    };
    if (this.isChoiceResetAllowed !== undefined && this.isChoiceResetAllowed !== null){
      dto.isChoiceResetAllowed = this.isChoiceResetAllowed;
    }
    if (this.isMultiLineTextEntryAllowed !== undefined && this.isMultiLineTextEntryAllowed !== null){
      dto.isMultiLineTextEntryAllowed = this.isMultiLineTextEntryAllowed;
    }
    if (this.isUnanswered !== undefined && this.isUnanswered !== null){
      dto.isUnanswered = this.isUnanswered;
    }
    return dto;
  }
  private static validateOptions = (options: OpenEndedFieldV1[]): void => {
    if(options.length < 1) {
      throw new InvalidOpenEndedQuestionV1OptionsMinSizeError(
        1,
        `Attribute options is too short. Size is ${options.length}. Min size is 1.`
      );
    }
    if(options.length > 16) {
      throw new InvalidOpenEndedQuestionV1OptionsMaxSizeError(
        16,
        `Attribute options is too long. Size is ${options.length}. Max size is 16.`
      );
    }
  }
  
}

OpenEndedQuestionV1.prototype.toString = function toString(): string {
  return `OpenEndedQuestionV1(options='${this.options}',isChoiceResetAllowed='${this.isChoiceResetAllowed}',isMultiLineTextEntryAllowed='${this.isMultiLineTextEntryAllowed}',isUnanswered='${this.isUnanswered}')`;
};

export class InvalidOpenEndedQuestionV1Error extends Error {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidOpenEndedQuestionV1Error";
  }
}
export class InvalidOpenEndedQuestionV1OptionsError extends InvalidOpenEndedQuestionV1Error {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidOpenEndedQuestionV1OptionsError";
  }
}

export class InvalidOpenEndedQuestionV1OptionsMaxSizeError extends InvalidOpenEndedQuestionV1OptionsError {
  readonly maxSize: number;
  constructor(maxSize: number, message?: string) {
    super(message);
    this.maxSize = maxSize;
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidOpenEndedQuestionV1OptionsMaxSizeError";
  }
}

export class InvalidOpenEndedQuestionV1OptionsMinSizeError extends InvalidOpenEndedQuestionV1OptionsError {
  readonly minSize: number;
  constructor(minSize: number, message?: string) {
    super(message);
    this.minSize = minSize;
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidOpenEndedQuestionV1OptionsMinSizeError";
  }
}

export class InvalidOpenEndedQuestionV1IsChoiceResetAllowedError extends InvalidOpenEndedQuestionV1Error {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidOpenEndedQuestionV1IsChoiceResetAllowedError";
  }
}

export class InvalidOpenEndedQuestionV1IsMultiLineTextEntryAllowedError extends InvalidOpenEndedQuestionV1Error {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidOpenEndedQuestionV1IsMultiLineTextEntryAllowedError";
  }
}

export class InvalidOpenEndedQuestionV1IsUnansweredError extends InvalidOpenEndedQuestionV1Error {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidOpenEndedQuestionV1IsUnansweredError";
  }
}
